import { EM_DASH } from '../constants.js'

export interface RefrigerantRow {
    refrigerantName:string;
    purchased:number;   // lbs
    recovered:number;   // lbs
    currentInventory:number; // lbs
}

export interface ReconciliationRow {
    refrigerantName:string;
    purchased:number;
    recovered:number;
    currentInventory:number;
    impliedUsage:number;
    hasGap:boolean;
}

export type RiskLevel = 'low' | 'moderate' | 'high' | 'critical'

export interface ReconciliationResult {
    rows:ReconciliationRow[];
    totalPurchased:number;
    totalRecovered:number;
    totalInventory:number;
    totalImpliedUsage:number;
    hasAnyGap:boolean;
    riskLevel:RiskLevel;
    recommendations:string[];
}

function computeRisk (
    hasAnyGap:boolean,
    totalImpliedUsage:number,
    totalPurchased:number
):RiskLevel {
    if (hasAnyGap) return 'critical'
    if (totalPurchased === 0) return 'low'
    const usageFraction = totalImpliedUsage / totalPurchased
    if (usageFraction > 0.5) return 'high'
    if (usageFraction > 0.2) return 'moderate'
    return 'low'
}

function buildRecommendations (
    riskLevel:RiskLevel,
    hasAnyGap:boolean
):string[] {
    const recs:string[] = []

    if (hasAnyGap) {
        recs.push(
            'Review purchase records and inventory counts ' + EM_DASH + ' at least one ' +
            'refrigerant shows more accounted for than purchased.'
        )
        recs.push(
            'Verify opening inventory balances are included in your ' +
            'reconciliation.'
        )
    }

    if (riskLevel === 'high' || riskLevel === 'critical') {
        recs.push(
            'Conduct a thorough leak survey across all equipment using the ' +
            'affected refrigerants.'
        )
        recs.push(
            'Implement a refrigerant tracking log for every service event.'
        )
    }

    if (riskLevel === 'moderate') {
        recs.push(
            'Review service logs for any unrecorded refrigerant additions or ' +
            'recoveries.'
        )
    }

    if (riskLevel !== 'low') {
        recs.push(
            'Consider a pre-audit internal review before an EPA inspection.'
        )
    }

    return recs
}

export function calcInventoryReconciliation (
    rows:RefrigerantRow[]
):ReconciliationResult {
    const reconRows:ReconciliationRow[] = rows.map(row => {
        const impliedUsage =
            row.purchased - row.recovered - row.currentInventory
        return {
            refrigerantName: row.refrigerantName,
            purchased: row.purchased,
            recovered: row.recovered,
            currentInventory: row.currentInventory,
            impliedUsage,
            hasGap: impliedUsage < 0,
        }
    })

    const totalPurchased = reconRows.reduce((s, r) => s + r.purchased, 0)
    const totalRecovered = reconRows.reduce((s, r) => s + r.recovered, 0)
    const totalInventory = reconRows.reduce(
        (s, r) => s + r.currentInventory, 0
    )
    const totalImpliedUsage = reconRows.reduce(
        (s, r) => s + r.impliedUsage, 0
    )
    const hasAnyGap = reconRows.some(r => r.hasGap)

    const riskLevel = computeRisk(hasAnyGap, totalImpliedUsage, totalPurchased)
    const recommendations = buildRecommendations(riskLevel, hasAnyGap)

    return {
        rows: reconRows,
        totalPurchased,
        totalRecovered,
        totalInventory,
        totalImpliedUsage,
        hasAnyGap,
        riskLevel,
        recommendations,
    }
}
